/**
 * DBML View Component
 * Generates and displays DBML (Database Markup Language) syntax from entity schema
 */

import React, { useMemo, useState } from 'react';
import { Copy, Check, FileCode } from 'lucide-react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';

interface EntityDBMLViewProps {
  entities: Array<{ id: string; entity_type: string; data: any; source_artifact_id?: string }>;
  entityLabels: Record<string, string>;
  connections: Array<{
    sourceType: string;
    targetType: string;
    connectingFields: string[];
    confidence: number;
  }>;
}

// Generate DBML syntax from entities and connections
function generateDBML(
  entities: Array<{ id: string; entity_type: string; data: any }>,
  entityLabels: Record<string, string>,
  connections: Array<{
    sourceType: string;
    targetType: string;
    connectingFields: string[];
    confidence: number;
  }>
): string {
  // Group entities by type
  const entitiesByType: Record<string, Array<{ id: string; data: any }>> = {};
  entities.forEach(e => {
    if (!entitiesByType[e.entity_type]) {
      entitiesByType[e.entity_type] = [];
    }
    entitiesByType[e.entity_type].push(e);
  });

  const lines: string[] = [];
  lines.push('// Generated DBML Schema');
  lines.push('// Based on entity relationships detected from data');
  lines.push('');

  // Generate table definitions
  Object.keys(entitiesByType).sort().forEach(entityType => {
    const entitiesOfType = entitiesByType[entityType];
    const label = entityLabels[entityType] || entityType;
    
    // Get all unique fields from all entities of this type
    const allFields = new Set<string>();
    entitiesOfType.forEach(e => {
      Object.keys(e.data || {}).forEach(field => allFields.add(field));
    });

    // Get connecting fields for this entity type
    const connectingFields = new Set<string>();
    connections.forEach(conn => {
      if (conn.sourceType === entityType || conn.targetType === entityType) {
        conn.connectingFields.forEach(f => connectingFields.add(f));
      }
    });

    // Start table definition
    lines.push(`Table ${entityType} {`);
    lines.push(`  Note: "${label}"`);
    
    // Add fields, prioritizing connecting fields
    const sortedFields = Array.from(allFields).sort((a, b) => {
      const aIsConnecting = connectingFields.has(a);
      const bIsConnecting = connectingFields.has(b);
      if (aIsConnecting && !bIsConnecting) return -1;
      if (!aIsConnecting && bIsConnecting) return 1;
      return a.localeCompare(b);
    });

    sortedFields.forEach(field => {
      const isConnecting = connectingFields.has(field);
      const isIdField = field.includes('_id') || field === 'id';
      
      // Infer type from field name and sample values
      let fieldType = 'string';
      const sampleValues = entitiesOfType
        .slice(0, 10)
        .map(e => e.data?.[field])
        .filter(v => v !== null && v !== undefined);
      
      if (sampleValues.length > 0) {
        const firstValue = sampleValues[0];
        if (typeof firstValue === 'number') {
          if (Number.isInteger(firstValue)) {
            fieldType = 'integer';
          } else {
            fieldType = 'float';
          }
        } else if (typeof firstValue === 'boolean') {
          fieldType = 'boolean';
        } else if (firstValue instanceof Date || (typeof firstValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(firstValue))) {
          fieldType = 'date';
        }
      }

      // Mark primary/foreign keys
      let fieldDef = `  ${field} ${fieldType}`;
      if (isIdField && field === 'id') {
        fieldDef += ' [pk]';
      } else if (isConnecting) {
        fieldDef += ' [note: "🔗 Linking field"]';
      }
      
      lines.push(fieldDef);
    });

    lines.push('}');
    lines.push('');
  });

  // Generate relationships
  if (connections.length > 0) {
    lines.push('// Relationships');
    connections.forEach((conn, idx) => {
      const sourceLabel = entityLabels[conn.sourceType] || conn.sourceType;
      const targetLabel = entityLabels[conn.targetType] || conn.targetType;
      const primaryField = conn.connectingFields[0];
      
      // Determine relationship type (one-to-many by default)
      // Use > for one-to-many, < for many-to-one, - for one-to-one
      const relType = '>'; // Default to one-to-many
      
      lines.push(`Ref: ${conn.sourceType}.${primaryField} ${relType} ${conn.targetType}.${primaryField} [note: "${sourceLabel} → ${targetLabel} via ${primaryField}${conn.connectingFields.length > 1 ? ` (+${conn.connectingFields.length - 1} more)` : ''}"]`);
    });
  }

  return lines.join('\n');
}

export function EntityDBMLView({
  entities,
  entityLabels,
  connections,
}: EntityDBMLViewProps) {
  const [copied, setCopied] = useState(false);

  const dbmlContent = useMemo(() => {
    return generateDBML(entities, entityLabels, connections);
  }, [entities, entityLabels, connections]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(dbmlContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy DBML:', error);
    }
  };

  if (entities.length === 0) {
    return (
      <Card>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">No entities to generate DBML schema</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileCode className="h-5 w-5 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">DBML Schema</h2>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleCopy}
          className="flex items-center gap-2"
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              Copy DBML
            </>
          )}
        </Button>
      </div>

      <div className="relative">
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
          <code>{dbmlContent}</code>
        </pre>
      </div>

      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm text-blue-900 dark:text-blue-200">
          <strong>DBML (Database Markup Language)</strong> is a simple, readable syntax for defining database schemas.
          You can use this schema with tools like <a href="https://dbdiagram.io" target="_blank" rel="noopener noreferrer" className="underline">dbdiagram.io</a> to visualize your data structure.
        </p>
      </div>
    </Card>
  );
}

